import React, { useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components/macro";
import { v4 as uuidv4 } from "uuid";

import { PreventPropagation } from "components/Events";
import { ReactSelect } from "components/SearchableSelector";

import { useDispatch, useSelector } from "react-redux";

import { SaleAttributesAction } from "actions/saleAttributes";
import { getSaleById } from "selectors";
import {
  mergeAssignedUserOptions,
  SaleRoleUserOption,
  selectSaleRoleNameByUserId,
  selectSaleRoleUserOptions,
} from "selectors/saleRoles";

export enum SaleRoleKind {
  Managers = "managers",
  Assignees = "assignees",
}

const idField = (roleKind: SaleRoleKind) =>
  roleKind === SaleRoleKind.Managers ? "managerIds" : "assigneeIds";

// Min width so the popup stays usable when nothing is selected.
const EditorWrapper = styled.div`
  min-width: 260px;
  width: 100%;
  // Pin font-size to the app base so it doesn't shrink to the grid cell font.
  font-size: ${({ theme }) => theme.fontSizes.beta}px;
`;

const RolesText = styled.span<{ $empty?: boolean }>`
  display: inline-block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: ${({ $empty }) => ($empty ? "pointer" : "default")};
  color: ${({ $empty, theme }) => ($empty ? theme.colors.gray8C : "inherit")};
`;

interface SaleRolesSelectProps {
  saleId: number;
  roleKind: SaleRoleKind;
  editable: boolean;
  onEditComplete?: () => void;
}

/**
 * Display / edit the managers or assignees of a sale. Editing creates or
 * PATCHes the SaleAttributes row — the same per-(sale, owner) row that carries
 * the custom Sale Status — and the sales reducer applies the optimistic update.
 */
export function SaleRolesSelect({
  saleId,
  roleKind,
  editable,
  onEditComplete,
}: SaleRolesSelectProps): React.JSX.Element {
  const dispatch = useDispatch();
  const livestockSale = useSelector<RootState, Sale>(getSaleById(saleId));
  const userOptions = useSelector(selectSaleRoleUserOptions);
  const nameByUserId = useSelector(selectSaleRoleNameByUserId);

  const saleAttributes = livestockSale?.sale_attributes || null;
  const currentUserIds = useMemo<number[]>(
    () => saleAttributes?.[idField(roleKind)] || [],
    [saleAttributes, roleKind],
  );

  const isEditing = Boolean(onEditComplete);

  const options = useMemo<SaleRoleUserOption[]>(
    () =>
      mergeAssignedUserOptions(userOptions, currentUserIds, nameByUserId, {
        sort: true,
      }),
    [userOptions, currentUserIds, nameByUserId],
  );

  const selectedOptions = useMemo<SaleRoleUserOption[]>(
    () =>
      currentUserIds.map(id => ({ value: id, label: nameByUserId[id] || "" })),
    [currentUserIds, nameByUserId],
  );

  // Buffer edits locally and persist once when the editor closes, rather than a
  // backend round-trip on every add/remove.
  const [pending, setPending] = useState<SaleRoleUserOption[]>(selectedOptions);

  const applyRoles = (chosen: SaleRoleUserOption[]) => {
    const livestockSaleId = livestockSale.livestocksale_id;
    const ids = chosen.map(option => option.value);

    // Roles share the SaleAttributes row (and id) with the status, so reuse it.
    const attributesId = saleAttributes?.id || null;
    const roleFields =
      roleKind === SaleRoleKind.Managers
        ? { managerIds: ids }
        : { assigneeIds: ids };
    const base = {
      livestockSaleId,
      // Preserve the current status so a role edit doesn't clear it.
      saleStatusId: saleAttributes?.saleStatusId ?? null,
      ...roleFields,
    };

    dispatch(
      attributesId
        ? SaleAttributesAction.update({ id: attributesId, ...base })
        : SaleAttributesAction.create({ id: uuidv4(), ...base }),
    );
  };

  // Persist on teardown (covers blur, outside-click and Enter/Escape close),
  // and only when the selection actually changed. A ref keeps the latest
  // pending/current values so the unmount cleanup runs exactly once.
  const commitRef = useRef<() => void>(() => undefined);
  commitRef.current = () => {
    const nextIds = pending.map(option => option.value);
    const unchanged =
      nextIds.length === currentUserIds.length &&
      [...nextIds].sort((a, b) => a - b).join(",") ===
        [...currentUserIds].sort((a, b) => a - b).join(",");
    if (!unchanged) {
      applyRoles(pending);
    }
  };

  useEffect(() => {
    if (!isEditing) {
      return undefined;
    }
    return () => commitRef.current();
  }, [isEditing]);

  const namesLabel = currentUserIds
    .map(id => nameByUserId[id])
    .filter(Boolean)
    .join(", ");

  if (!editable || !isEditing) {
    return (
      <RolesText $empty={!namesLabel} title={namesLabel}>
        {namesLabel || (editable ? "Add…" : "—")}
      </RolesText>
    );
  }

  return (
    <PreventPropagation>
      {/* ag-custom-component-popup keeps clicks (incl. the menu, rendered
          inside this wrapper) from being treated as outside the grid editor. */}
      <EditorWrapper className="ag-custom-component-popup">
        <ReactSelect
          isMulti
          isClearable
          autoFocus
          defaultMenuIsOpen
          openMenuOnFocus
          closeMenuOnSelect={false}
          menuPlacement="auto"
          placeholder="Select…"
          options={options}
          value={pending}
          onChange={(selected: SaleRoleUserOption[] | null) =>
            setPending(selected || [])
          }
          onBlur={onEditComplete}
        />
      </EditorWrapper>
    </PreventPropagation>
  );
}
